<template>
    <div class="visor-root">
        <div class="visor-toolbar">
            <div class="row items-center q-gutter-x-sm">
                <q-icon name="mdi-file-pdf-box" size="28px" color="red-4" />
                <div class="column">
                    <span class="text-weight-bold text-grey-9" style="font-size:14px;line-height:1.2">
                        Estudio Socioeconomico
                    </span>
                    <span class="text-grey-5" style="font-size:11px;line-height:1.2">
                        {{ paciente?.nombres }} {{ paciente?.apellidoPaterno }} {{ paciente?.apellidoMaterno }}
                    </span>
                </div>
            </div>
            <q-space />
            <div v-if="totalPaginas > 0" class="row items-center q-gutter-x-sm q-mr-sm">
                <q-btn flat round dense color="grey-7" icon="mdi-chevron-left" :disable="paginaActual <= 1"
                    @click="cambiarPagina(paginaActual - 1)" />
                <span class="text-grey-7" style="font-size:13px">{{ paginaActual }} / {{ totalPaginas }}</span>
                <q-btn flat round dense color="grey-7" icon="mdi-chevron-right" :disable="paginaActual >= totalPaginas"
                    @click="cambiarPagina(paginaActual + 1)" />
            </div>
            <q-btn-group flat>
                <q-btn flat round color="grey-7" icon="mdi-refresh" :loading="cargando" @click="generarVista()">
                    <q-tooltip>Generar vista previa</q-tooltip>
                </q-btn>
                <q-btn flat round color="grey-7" icon="mdi-download" :disable="!pdfCargado" @click="descargar()">
                    <q-tooltip>Descargar</q-tooltip>
                </q-btn>
                <q-btn flat round color="grey-7" icon="mdi-printer" :disable="!pdfCargado" @click="imprimir()">
                    <q-tooltip>Imprimir</q-tooltip>
                </q-btn>
            </q-btn-group>
        </div>

        <div class="visor-body">

            <!-- Vacío -->
            <div v-if="!pdfCargado && !cargando" class="visor-empty">
                <div class="empty-card">
                    <q-icon name="mdi-file-pdf-box" size="80px" color="red-3" />
                    <p class="text-h6 text-grey-7 q-mt-md q-mb-xs">Sin vista previa</p>
                    <p class="text-grey-5 text-center q-mb-lg" style="max-width:280px">
                        Genera el documento para previsualizarlo aquí.
                    </p>
                    <q-btn unelevated color="primary" icon="mdi-file-eye" label="Generar vista previa" rounded
                        @click="generarVista()" />
                </div>
            </div>

            <!-- Cargando -->
            <div v-if="cargando" class="visor-empty">
                <div class="empty-card">
                    <q-spinner-dots color="primary" size="60px" />
                    <p class="text-grey-6 q-mt-md">Generando documento...</p>
                </div>
            </div>

            <!-- PDF: v-show para que el canvas siempre esté en el DOM -->
            <div v-show="pdfCargado && !cargando" class="visor-pdf-wrapper">
                <div class="pdf-shadow-container">
                    <canvas ref="pdfCanvas" />
                </div>
            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, computed, onUnmounted, nextTick } from "vue"
import { useStore } from "vuex"
import axios from "axios"
import * as pdfjsLib from "pdfjs-dist"
import { useQuasar, Notify, QSpinnerFacebook } from "quasar"
import { generarFichaSocioeconomica } from '../PDF/EstudioSocioeconomicoPDF.js'

pdfjsLib.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs`

const $q = useQuasar()
const store = useStore()
const estudioSocio = ref(null)
const escala = ref('');

const paciente = computed(() => store.state.pacienteStore)
const pdfCanvas = ref(null)
const pdfCargado = ref(false)
const cargando = ref(false)
const paginaActual = ref(1)
const totalPaginas = ref(0)
const base64Guardado = ref(null)
let pdfDoc = null

async function generarVista() {
    try {
        if(paciente.value.socioEconomico == false){
            $q.notify({ type: 'info', message: 'El paciente no cuenta con estudio socieconomico', position:'top-right' })
            return
        }

        cargando.value = true
        pdfDoc = null
        pdfCargado.value = false

        await getEstudioSocioEconomico()
        let base64 = await generarFichaSocioeconomica(paciente.value, estudioSocio.value, escala.value)
        base64Guardado.value = base64

        const limpio = base64.replace('data:application/pdf;filename=generated.pdf;base64,', '')
        pdfDoc = await pdfjsLib.getDocument({ data: atob(limpio) }).promise

        totalPaginas.value = pdfDoc.numPages
        paginaActual.value = 1

        pdfCargado.value = true
        await nextTick()

        await renderPagina(1)
    } catch (e) {
        console.error(e)
        $q.notify({ type: 'negative', message: 'Error al generar el PDF' })
    } finally {
        cargando.value = false
    }
}

async function renderPagina(num) {
    if (!pdfDoc || !pdfCanvas.value) return
    const page = await pdfDoc.getPage(num)
    const scale = 2
    const scaled = page.getViewport({ scale })
    const canvas = pdfCanvas.value
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    canvas.width = scaled.width
    canvas.height = scaled.height
    await page.render({ canvasContext: ctx, viewport: scaled }).promise
}

async function cambiarPagina(num) {
    paginaActual.value = num
    await renderPagina(num)
}

function descargar() {
    if (!base64Guardado.value) return
    const nombre = `AvisoPrivacidad_${paciente.value?.nombres ?? ''}_${paciente.value?.apellidoPaterno ?? ''}.pdf`
    const a = document.createElement('a')
    a.href = base64Guardado.value
    a.download = nombre
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

function imprimir() {
    if (!base64Guardado.value) return
    const limpio = base64Guardado.value.replace('data:application/pdf;filename=generated.pdf;base64,', '')
    const blob = new Blob([Uint8Array.from(atob(limpio), c => c.charCodeAt(0))], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const win = window.open(url)
    win.onload = () => { win.print(); URL.revokeObjectURL(url) }
}

const getEstudioSocioEconomico = async () => {
    $q.loading.show({
        spinner: QSpinnerFacebook,
        spinnerColor: 'primary',
        spinnerSize: 100,
        message: 'Consultando estudio socioeconomico...'
    })
    try {
        const response = await axios.get('EstudioSocioEconomico/Paciente/' + paciente.value.id)
        console.log('Respuesta backend:', response.data)
        estudioSocio.value = response.data[0]

        if (response.data.length != 0) {
            let total = estudioSocio.value.grupoFamiliar.calificacion +
                estudioSocio.value.tocupacion.calificacion +
                estudioSocio.value.salario.calificacion +
                estudioSocio.value.situacionEconomica.calificacion +
                estudioSocio.value.tipoVivienda.calificacion +
                estudioSocio.value.numeroDormitorios.calificacion
            console.log(total)
            if (total <= 3) {
                escala.value = 'Exento'
            } else if (total >= 4 && total <= 6) {
                escala.value = '1'

            } else if (total >= 7 && total <= 9) {
                escala.value = '2'

            }
            else if (total >= 10 && total <= 12) {
                escala.value = '3'
            }
            else if (total >= 13 && total <= 17) {
                escala.value = '4'

            }
            else if (total >= 18 && total <= 21) {
                escala.value = '5'

            }
            else if (total >= 22 && total <= 25) {
                escala.value = '6'

            }
        }
    } catch (error) {
        console.error(error)
        Notify.create({
            message: 'Error al consultar la información',
            color: 'negative',
            position: 'top-right'
        })
    } finally {
        $q.loading.hide()
    }
}




onUnmounted(() => { pdfDoc?.destroy() })
</script>
<style scoped>
.visor-root {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: #f0f2f5;
}

.visor-toolbar {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    background: #fff;
    border-bottom: 1px solid #e8eaed;
    box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
    z-index: 10;
}

.visor-body {
    flex: 1;
    overflow: hidden;
    position: relative;
}

.visor-empty {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.empty-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: #fff;
    border-radius: 16px;
    padding: 48px 40px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, .07);
}

.visor-pdf-wrapper {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    overflow-y: auto;
    overflow-x: hidden;
}

.pdf-shadow-container {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 32px rgba(0, 0, 0, .15);
    flex-shrink: 0;
    width: 100%;
}

canvas {
    display: block;
    width: 100% !important;
    height: auto !important;
}

.border-right {
    border-right: 1px solid #e8eaed;
}

canvas {
    display: block;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity .25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-up-enter-active {
    transition: all .35s cubic-bezier(.16, 1, .3, 1);
}

.slide-up-enter-from {
    opacity: 0;
    transform: translateY(20px);
}
</style>